import { Component, createContext, lazy, Suspense, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import styles from './index.module.css'
import { getEnvType } from 'config'

// The launcher's code, its session and its polling wait for the page to settle, so a first
// visit's own requests go first. Asking for chat (useChat().open) starts it at once.
const WallyChatLauncher = lazy(() =>
  import('@wallyhealth/wally-widget-react').then(module => ({ default: module.WallyChatLauncher }))
)

const START_AFTER_LOAD_MS = 2000
const IDLE_TIMEOUT_MS = 2000
// How long the open chat takes to tuck into the Chat control when a step needs its panel back.
export const TUCK_MS = 220
// A flow's open chat takes the whole screen below this width, where its layout has no panel.
export const FLOW_FULL_SCREEN_BELOW = 768
export const TUCKED_NOTE = 'Chat closed. Open Chat to pick up where you left off.'

// The widget's Wally theme: the brand's palette and 4px corners, as every control on these pages.
const THEME = { name: 'wally', radius: 4 }

/** A launcher that fails to load (offline, blocked) leaves the page without chat, not blank. */
class ChatBoundary extends Component {
  constructor (props) {
    super(props)
    this.state = { hasFailed: false }
  }

  static getDerivedStateFromError () {
    return { hasFailed: true }
  }

  componentDidCatch () {
    this.props.onFail()
  }

  render () {
    return this.state.hasFailed ? null : this.props.children
  }
}

/** Calls `start` a while after the window's load event, once the browser is idle. */
const afterPageSettles = (start) => {
  let timer = null
  let idle = null
  const schedule = () => {
    timer = setTimeout(() => {
      if (window.requestIdleCallback) idle = window.requestIdleCallback(start, { timeout: IDLE_TIMEOUT_MS })
      else start()
    }, START_AFTER_LOAD_MS)
  }
  if (document.readyState === 'complete') schedule()
  else window.addEventListener('load', schedule, { once: true })
  return () => {
    window.removeEventListener('load', schedule)
    clearTimeout(timer)
    if (idle !== null) window.cancelIdleCallback?.(idle)
  }
}

const ChatContext = createContext({
  open: () => {},
  close: () => {},
  toggle: () => {},
  isOpen: false,
  isAvailable: false,
  status: { hasUnread: false, hasError: false },
  registerAnchor: () => () => {},
  registerColumn: () => () => {},
  hideVerification: () => () => {},
  yieldPanel: () => {}
})

export const useChat = () => useContext(ChatContext)

/**
 * One element at a time for a role (the header's Chat, the column chat opens into). A step that
 * mounts before the last one unmounts takes the role, and the last one's leaving doesn't undo it.
 */
const useSlot = () => {
  const [element, setElement] = useState(null)
  const register = useCallback((next) => {
    setElement(next)
    return () => setElement(current => (current === next ? null : current))
  }, [])
  return [element, register]
}

/** Where each scrolled element inside the chat had got to, to put back after a move. */
const scrollPositions = (root) => [...root.querySelectorAll('*')]
  .filter(element => element.scrollTop > 0)
  .map(element => [element, element.scrollTop])

/**
 * The one element the open chat is drawn into, for as long as the provider is up, and the frame
 * it fills: the frame a page registers (`registerColumn`), or none.
 *
 * Each step of a flow draws its own layout, so each has its own frame. Handing the widget a new
 * element on every step made it draw the chat again from nothing there: a message being typed, the
 * place the conversation was scrolled to and a half-typed code were lost at every Continue. The
 * widget is handed this element instead, which never changes, and the element is moved into the
 * step's frame. Moving it re-attaches the same chat, so React keeps all of it; only the scroll
 * positions need putting back, as moving an element resets them.
 */
const useChatFrame = () => {
  const [slot] = useState(() => {
    const element = document.createElement('div')
    element.className = styles.slot
    return element
  })
  const [frame, registerFrame] = useSlot()
  const scrolled = useRef([])

  // A frame's ref lets go before React takes the frame out of the page, so the chat is still
  // there to read.
  const registerColumn = useCallback((element) => {
    const release = registerFrame(element)
    return () => {
      scrolled.current = scrollPositions(slot)
      release()
    }
  }, [registerFrame, slot])

  useLayoutEffect(() => {
    if (!frame) {
      slot.remove()
      return
    }
    if (slot.parentNode !== frame) frame.appendChild(slot)
    scrolled.current.forEach(([element, top]) => { if (slot.contains(element)) element.scrollTop = top })
    scrolled.current = []
  }, [frame, slot])

  return { slot, frame, registerColumn }
}

/**
 * A ref for an element that fills a chat slot while it's mounted: `useChat().registerAnchor` for
 * the control that opens chat, `registerColumn` for the column it opens into.
 */
export const useChatSlot = (register) => {
  const release = useRef(null)
  return useCallback((element) => {
    release.current?.()
    release.current = element ? register(element) : null
  }, [register])
}

/**
 * For a page that verifies the visitor's email itself (sign-up, group activation): chat doesn't
 * offer its own Verify Email while the page is up. Chat still asks if the server needs it.
 */
export const useHideChatVerification = () => {
  const { hideVerification } = useChat()
  useEffect(() => hideVerification(), [hideVerification])
}

// A field in the chat, named as a member would know it.
const fieldName = (field) => field.getAttribute('aria-label') || field.name || field.placeholder || ''

/** What's typed in the chat's fields, by name: the message being written, a code half entered. */
const typedFields = (root) => [...root.querySelectorAll('textarea, input')]
  .filter(field => field.value && fieldName(field) && !['hidden', 'password', 'checkbox', 'radio'].includes(field.type))
  .map(field => [fieldName(field), field.value])

/**
 * Types `value` into a field the widget's React owns: through the element's own value setter, which
 * React watches, then the input event it listens for, so its state takes the value too.
 */
const typeInto = (field, value) => {
  const proto = field instanceof window.HTMLTextAreaElement ? window.HTMLTextAreaElement : window.HTMLInputElement
  Object.getOwnPropertyDescriptor(proto.prototype, 'value').set.call(field, value)
  field.dispatchEvent(new Event('input', { bubbles: true }))
}

// The chat's fields wherever the widget draws it: in the flow's panel (`slot`), or full screen in
// its own root on the page when the window has narrowed meanwhile.
const chatFields = (slot) => [slot, ...document.querySelectorAll('.ww-root')]
  .flatMap(root => [...root.querySelectorAll('textarea, input')])

/**
 * Puts back what was typed before a tuck, into the same empty fields of the chat drawn again, as
 * they appear (the widget may draw them a moment after it opens). Gives up after two seconds, and
 * hands back what it couldn't place (`onLeft`), to try again the next time chat opens.
 */
const restoreTyped = (slot, typed, onLeft) => {
  const left = new Map(typed)
  const fill = () => {
    chatFields(slot).forEach(field => {
      const value = left.get(fieldName(field))
      if (value !== undefined && !field.value) {
        typeInto(field, value)
        left.delete(fieldName(field))
      }
    })
    return left.size === 0
  }
  if (fill()) return () => {}
  const observer = new MutationObserver(() => { if (fill()) observer.disconnect() })
  observer.observe(document.body, { childList: true, subtree: true })
  const timer = setTimeout(() => observer.disconnect(), 2000)
  return () => {
    observer.disconnect()
    clearTimeout(timer)
    onLeft([...left])
  }
}

/**
 * A step that puts something to act on into the panel the open chat fills (a chosen visit and its
 * Book, a studio's address) asks for the panel back with `yieldPanel`. The chat tucks into the
 * header's Chat, shrinking towards it in TUCK_MS (fading only, with Reduce Motion), and closes;
 * Chat opens it again as it was. Only an open chat in a panel's frame yields: a chat in a column
 * of its own covers nothing, and one that fills the phone's screen is the member's whole view.
 *
 * Closing moves the focus where the widget always moves it, to Chat, which would take it from the
 * time just chosen. It goes back to wherever it was instead; if that was inside the chat, to what
 * the step names (`focusTarget`, an element or a function that finds one). A polite note says where
 * the chat went.
 *
 * Closing also drops what the chat's own fields held: the widget draws its panel again from nothing
 * when it opens. A member who was mid-message didn't choose to close it, so what was typed is kept
 * and put back when Chat opens it again. The widget isn't kept open out of sight instead: it counts
 * replies as read while it's open, and Chat's unread dot would never show.
 */
const useTuck = ({ slot, frame, anchor, isOpen, setIsOpen }) => {
  const timer = useRef(null)
  const focusAfter = useRef(null)
  const typed = useRef([])
  const [isTucking, setIsTucking] = useState(false)
  const [note, setNote] = useState('')

  const stop = useCallback(() => {
    clearTimeout(timer.current)
    timer.current = null
    slot.classList.remove(styles.slot_tucking)
    slot.style.transformOrigin = ''
    setIsTucking(false)
  }, [slot])

  // Read when a step asks, so `yieldPanel` stays the same function: a step's effect that depends on
  // it would otherwise run again as chat opens, and tuck away the chat the member just opened.
  const latest = useRef({})
  latest.current = { isOpen, frame, anchor }

  const yieldPanel = useCallback((focusTarget) => {
    const { isOpen, frame, anchor } = latest.current
    if (!isOpen || timer.current || frame?.dataset.chatFrame !== 'panel' || !slot.firstChild) return
    const active = document.activeElement
    const wasInChat = slot.contains(active)
    const from = slot.getBoundingClientRect()
    const to = anchor?.getBoundingClientRect()
    if (to) slot.style.transformOrigin = `${to.left + to.width / 2 - from.left}px ${to.top + to.height / 2 - from.top}px`
    slot.classList.add(styles.slot_tucking)
    setIsTucking(true)
    timer.current = setTimeout(() => {
      timer.current = null
      focusAfter.current = wasInChat ? (typeof focusTarget === 'function' ? focusTarget() : focusTarget) : active
      typed.current = typedFields(slot)
      setNote(TUCKED_NOTE)
      setIsOpen(false)
    }, TUCK_MS)
  }, [setIsOpen, slot])

  // Once the widget has closed, and moved the focus to Chat, the shrink is undone (the chat is no
  // longer drawn) and the focus goes back. The widget is a child, so its effects run first.
  useEffect(() => {
    if (isOpen) {
      setNote('')
      const carried = typed.current
      typed.current = []
      return carried.length ? restoreTyped(slot, carried, (left) => { typed.current = left }) : undefined
    }
    stop()
    const target = focusAfter.current
    focusAfter.current = null
    if (target?.isConnected) target.focus({ preventScroll: true })
  }, [isOpen, slot, stop])

  useEffect(() => () => clearTimeout(timer.current), [])

  return { yieldPanel, isTucking, note, stop }
}

/**
 * Chat on every page. Where the page has a Chat control of its own (the flow's header), the widget
 * draws no launcher: the control opens it, and the "can't connect" bubble hangs under it. Where the
 * page gives it a column, the open chat fills it on wider windows, and stays the same chat from one
 * step to the next (useChatFrame); phones always get the whole screen. Elsewhere it's the widget's
 * floating launcher.
 */
export const ChatProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isStarted, setIsStarted] = useState(false)
  const [isAvailable, setIsAvailable] = useState(true)
  const [status, setStatus] = useState({ hasUnread: false, hasError: false })
  const [anchor, registerAnchor] = useSlot()
  const { slot, frame, registerColumn } = useChatFrame()
  const [verificationHidden, setVerificationHidden] = useState(0)

  useEffect(() => afterPageSettles(() => setIsStarted(true)), [])

  const hideVerification = useCallback(() => {
    setVerificationHidden(count => count + 1)
    return () => setVerificationHidden(count => count - 1)
  }, [])

  const { yieldPanel, isTucking, note, stop } = useTuck({ slot, frame, anchor, isOpen, setIsOpen })

  const open = useCallback(() => {
    stop()
    setIsStarted(true)
    setIsOpen(true)
  }, [stop])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => {
    stop()
    setIsStarted(true)
    setIsOpen(current => !current)
  }, [stop])

  return (
    <ChatContext.Provider
      value={{
        open,
        close,
        toggle,
        isOpen,
        isAvailable,
        status,
        registerAnchor,
        registerColumn,
        hideVerification,
        yieldPanel,
        isTucking
      }}>
      {children}
      <p className={styles.note} role="status">{note}</p>
      {isStarted && isAvailable &&
        <div className={styles.dock}>
          <ChatBoundary onFail={() => setIsAvailable(false)}>
            <Suspense fallback={null}>
              <WallyChatLauncher
                environment={getEnvType() === 'PROD' ? 'prod' : 'stage'}
                enableAppointmentActions={window.__ENV === 'STAGE' && process.env.REACT_APP_STAGE_WIDGET_APPOINTMENT_ACTIONS_ENABLED === 'true'}
                theme={THEME}
                launcher={anchor ? 'none' : 'floating'}
                anchor={anchor}
                // A flow (its header has Chat) opens chat in its panel from a tablet's 768px
                // (FlowLayout); narrower than that there's no panel, so it takes the whole screen.
                // Other pages keep the widget's own 640px.
                fullScreenBelow={anchor ? FLOW_FULL_SCREEN_BELOW : undefined}
                container={frame ? slot : null}
                offerVerification={verificationHidden === 0}
                onStatusChange={setStatus}
                open={isOpen}
                onOpenChange={setIsOpen}
              />
            </Suspense>
          </ChatBoundary>
        </div>
      }
    </ChatContext.Provider>
  )
}

export default ChatProvider
